import * as React from 'react'

import {
  useCreateResourceLinkMutation,
  useDeleteResourceLinkMutation,
} from '~/client/graph/operations/generated/ResourceLinks.generated'
import type { ManagedResourceLinkKindId } from '~/client/src/shared/resources/resourceLink/managedResourceLinks'

interface UseResourceLinkParams {
  projectId: string
  kindId: ManagedResourceLinkKindId
}

interface LinkParams {
  sourceId: string
  targetId: string
}

interface UnlinkParams {
  sourceId: string
}

interface ResourceLinkResult {
  link: (
    params: LinkParams,
  ) => Promise<{ success: boolean; error?: string | null }>
  unlink: (
    params: UnlinkParams,
  ) => Promise<{ success: boolean; error?: string | null }>
  loading: boolean
}

export function useResourceLink({
  projectId,
  kindId,
}: UseResourceLinkParams): ResourceLinkResult {
  const [createLink, { loading: createLoading }] =
    useCreateResourceLinkMutation()
  const [deleteLink, { loading: deleteLoading }] =
    useDeleteResourceLinkMutation()

  const link = React.useCallback(
    async ({ sourceId, targetId }: LinkParams) => {
      const result = await createLink({
        variables: { projectId, kindId, sourceId, targetId },
      })

      const linkResult = result.data?.createResourceLink
      return {
        success: linkResult?.success ?? false,
        error: linkResult?.error ?? null,
      }
    },
    [projectId, kindId, createLink],
  )

  const unlink = React.useCallback(
    async ({ sourceId }: UnlinkParams) => {
      const result = await deleteLink({
        variables: { projectId, kindId, sourceId },
      })

      const linkResult = result.data?.deleteResourceLink
      return {
        success: linkResult?.success ?? false,
        error: linkResult?.error ?? null,
      }
    },
    [projectId, kindId, deleteLink],
  )

  return {
    link,
    unlink,
    loading: createLoading || deleteLoading,
  }
}
